import React from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import {
  Alert as MuiAlert,
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Typography as MuiTypography,
  Divider as MuiDivider,
  Grid,
  Paper as MuiPaper,
  TextField as MuiTextField,
  Toolbar,
} from "@mui/material";

import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { settingsMenu } from "../../../../../redux/slices/settings";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import {
  addLocationSettings,
  reset,
} from "../../../../../redux/slices/locationSettings";
import { isValidToken } from "../../../../../utils/jwt";
import {
  fetchLogOut,
  fetchLoginLogOut_Socket,
  logout,
} from "../../../../../redux/slices/login";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Card = styled(MuiCard)(spacing);

//const timeOut = (time) => new Promise((res) => setTimeout(res, time));

const initialValues = {
  location_name: "",
  description: "",
  icon: "",
};

const validationSchema = Yup.object().shape({
  location_name: Yup.string().max(50).required("Required"),
  // description: Yup.string().required('Required'),
});

const LocationSettingsAdd = () => {
  const dispatch = useDispatch();
  const accessToken = window.localStorage.getItem("accessToken");
  const navigate = useNavigate();

  React.useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setStatus, setSubmitting }
  ) => {
    const payload = {
      location_name: values.location_name,
      description: values.description,
      icon: values.icon,
    };

    try {
      if (!isValidToken(accessToken)) {
        dispatch(fetchLogOut());
        dispatch(fetchLoginLogOut_Socket());
        dispatch(logout());
        navigate("/");
      } else {
        setSubmitting(true); // Prevent multiple submissions
        dispatch(addLocationSettings(payload)).unwrap(); // Ensure the action is awaited properly
        resetForm(); // Reset the form after successful submission
        setStatus({ sent: true });
        toast.success("Location Settings added successfully");
        dispatch(
          settingsMenu({
            list: "locationsettings",
            listData: [],
          })
        );
      }
    } catch (error) {
      setStatus({ sent: false });
      setErrors({ submit: error.message });
      toast.error("Failed to add Location Settings");
    } finally {
      setSubmitting(false); // Reset submitting state
    }
  };

  const handleBack = () => {
    if (!isValidToken(accessToken)) {
      dispatch(fetchLogOut());
      dispatch(fetchLoginLogOut_Socket());
      dispatch(logout());
      navigate("/");
    } else {
      dispatch(
        settingsMenu({
          list: "locationsettings",
          listData: [],
        })
      );
    }
  };

  return (
    <Box>
      <Paper>
        <Toolbar>
          <Typography variant="h6" id="tableTitle">
            Add Location Settings{" "}
          </Typography>
          <Box flexGrow={1} />{" "}
          <div>
            <Button
              mr={2}
              variant="contained"
              color="primary"
              style={{ marginRight: "5px" }}
              onClick={() => {
                handleBack();
              }}
            >
              <NavigateBeforeIcon />
              Back{" "}
            </Button>{" "}
          </div>{" "}
        </Toolbar>{" "}
      </Paper>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        {" "}
        {/* Form Section */}{" "}
        <Grid item xs={12} lg={9}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              status,
            }) => (
              <React.Fragment>
                <Card sx={{ mb: 6 }}>
                  <CardContent>
                    {status && status.sent && (
                      <Alert severity="success" sx={{ my: 3 }}>
                        Your data has been submitted successfully!
                      </Alert>
                    )}
                    {isSubmitting ? (
                      <Box display="flex" justifyContent="center" my={6}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <form onSubmit={handleSubmit}>
                        <TextField
                          name="location_name"
                          label="Location Name"
                          value={values.location_name}
                          error={Boolean(
                            touched.location_name && errors.location_name
                          )}
                          fullWidth
                          helperText={
                            touched.location_name && errors.location_name
                          }
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type="text"
                          variant="outlined"
                          sx={{ my: 2 }}
                        />
                        <TextField
                          name="description"
                          label="Description"
                          value={values.description}
                          error={Boolean(
                            touched.description && errors.description
                          )}
                          fullWidth
                          helperText={touched.description && errors.description}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type="text"
                          variant="outlined"
                          sx={{ my: 2 }}
                        />
                        <TextField
                          name="icon"
                          label="Icon"
                          value={values.icon}
                          error={Boolean(touched.icon && errors.icon)}
                          fullWidth
                          helperText={touched.icon && errors.icon}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type="text"
                          variant="outlined"
                          sx={{ my: 2 }}
                        />
                        <Box mt={3}>
                          <Button
                            color="primary"
                            disabled={isSubmitting}
                            size="large"
                            type="submit"
                            variant="contained"
                          >
                            Submit
                          </Button>
                        </Box>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </React.Fragment>
            )}
          </Formik>
        </Grid>
        {/* Sidebar Section */}{" "}
        <Grid item xs={12} lg={3}>
          <Box
            sx={{
              padding: "10px",
              backgroundColor: "#F9F9F9",
              height: "92vh",
            }}
          >
            <Typography gutterBottom variant="h5" component="h2">
              Location Settings{" "}
            </Typography>{" "}
            <Typography component="p">
              A Location Settings tracks facts about your Location Setting— like
              the Location Name, or description of location and icon.{" "}
            </Typography>{" "}
            <Typography component="p">
              For creating a Custom Attribute, just click on the Add Location
              Settings.You can also edit or delete an existing Location Setting
              by clicking on the Edit or Delete button.{" "}
            </Typography>{" "}
          </Box>{" "}
        </Grid>{" "}
      </Grid>{" "}
    </Box>
  );
};

export default LocationSettingsAdd;
